import { supabase } from '../lib/supabaseClient'

/**
 * Utilitários para gerenciar problemas de autenticação
 */
export const authUtils = {
  /**
   * Limpa completamente a sessão do Supabase
   * Útil quando há problemas com refresh tokens corrompidos
   */
  async clearSession(): Promise<void> {
    try {
      // Fazer logout do Supabase
      await supabase.auth.signOut()
      
      // Limpar localStorage relacionado ao Supabase
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith('sb-') || key.includes('supabase')) {
          localStorage.removeItem(key)
        }
      })
      
      // Limpar sessionStorage relacionado ao Supabase
      const sessionKeys = Object.keys(sessionStorage)
      sessionKeys.forEach(key => {
        if (key.startsWith('sb-') || key.includes('supabase')) {
          sessionStorage.removeItem(key)
        }
      })
      
      console.log('✅ Sessão limpa com sucesso')
    } catch (error) {
      console.error('Erro ao limpar sessão:', error)
    }
  },

  /**
   * Verifica se há problemas com o refresh token
   */
  async checkRefreshToken(): Promise<boolean> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('Erro ao verificar sessão:', error)
        return false
      }
      
      if (!session) {
        console.log('Nenhuma sessão ativa')
        return false
      }
      
      // Tentar renovar o token
      const { error: refreshError } = await supabase.auth.refreshSession()
      
      if (refreshError) {
        console.error('Erro ao renovar token:', refreshError)
        return false
      }
      
      return true
    } catch (error) {
      console.error('Erro ao verificar refresh token:', error)
      return false
    }
  },

  /**
   * Força um novo login após limpar a sessão
   */
  async forceReauth(): Promise<void> {
    await this.clearSession()
    
    // Recarregar a página para garantir estado limpo
    window.location.href = '/login'
  },

  /**
   * Monitora erros de autenticação e tenta resolver automaticamente
   */
  setupAuthErrorHandler(): void {
    // Interceptar erros do Supabase
    const originalConsoleError = console.error
    console.error = (...args) => {
      const message = args.join(' ')
      
      // Detectar erros de refresh token
      if (message.includes('Invalid Refresh Token') || 
          message.includes('Refresh Token Not Found')) {
        console.warn('🔄 Detectado erro de refresh token, limpando sessão...')
        this.forceReauth()
      }
      
      // Chamar console.error original
      originalConsoleError.apply(console, args)
    }
    
    // Interceptar erros não capturados
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason
      if (error?.message?.includes('Invalid Refresh Token') ||
          error?.message?.includes('Refresh Token Not Found')) {
        console.warn('🔄 Detectado erro de refresh token não capturado, limpando sessão...')
        this.forceReauth()
      }
    })
  }
}

// Configurar o handler automaticamente
if (typeof window !== 'undefined') {
  authUtils.setupAuthErrorHandler()
}